import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as MuiCore from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/Info';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import PersonIcon from '@material-ui/icons/Person';

const fieldTypes = [
    { value: 'text',        label: 'Text',   Icon: TextFieldsIcon },
    { value: 'email',       label: 'Email',  Icon: EmailIcon },
    { value: 'phoneNumber', label: 'Phone', Icon: PhoneIcon },
    { value: 'caller',      label: 'Caller', Icon: PersonIcon },
];

const FormFieldsFormCard = ({
    form,
    onCancel,
    onRefreshForm,
    createFormFieldAsync,
    updateManyFormFieldsAsync,
    deleteFormFieldAsync,
    moveFormFieldsAsync,
    createFormVersionAsync,
    activateFormVersionAsync,
    classes
}) => {
    const [draftVersion, setDraftVersion] = useState(null);
    const [fields, setFields] = useState([]);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState({});
    const [creating, setCreating] = useState(false);
    const [isReordering, setIsReordering] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // Drag state
    const dragIndexRef = useRef(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleCreateDraft = useCallback(async () => {
        if (!form?.activeVersion?.formVersionId) return;
        try {
            setCreating(true);
            await createFormVersionAsync(form.activeVersion.formVersionId);
            onRefreshForm?.();
        } catch (error) {
            console.error('Failed to create draft:', error);
        } finally {
            setCreating(false);
        }
    }, [form, createFormVersionAsync, onRefreshForm]);

    useEffect(() => {
        if (form) {
            if (form.draftVersion) {
                setDraftVersion(form.draftVersion);
                setFields(form.draftVersion.formFields || []);
            } else if (form.activeVersion) {
                handleCreateDraft();
            }
        }
    }, [form, handleCreateDraft]);

    const handleAddField = async () => {
        if (!draftVersion?.formVersionId) return;
        const newField = {
            formVersionId: draftVersion.formVersionId,
            label: '',
            fieldType: 'text',
            required: 'Y',
            verify: 'N',
        };
        try {
            const createdField = await createFormFieldAsync(newField);
            setFields([...fields, createdField]);
        } catch (error) {
            console.error('Failed to create field:', error);
        }
    };

    const handleFieldChange = (index, fieldName, value) => {
        const updatedFields = [...fields];
        updatedFields[index] = { ...updatedFields[index], [fieldName]: value };
        setFields(updatedFields);
        setHasUnsavedChanges(prev => ({ ...prev, [index]: true }));
        if (fieldName === 'label' && value.trim()) {
            setValidationErrors(prev => { const next = { ...prev }; delete next[index]; return next; });
        }
    };

    const validate = () => {
        const errors = {};
        fields.forEach((field, index) => {
            if (!field.label || !field.label.trim()) {
                errors[index] = 'Field name is required';
            }
        });
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleDeleteField = async (index) => {
        const field = fields[index];
        if (field.formFieldId) {
            try {
                await deleteFormFieldAsync(field.formFieldId);
                setFields(fields.filter((_, i) => i !== index));
            } catch (error) {
                console.error('Failed to delete field:', error);
            }
        }
    };

    const handleSaveDraft = async () => {
        if (!validate()) return;
        const changedFields = fields.filter((_, i) => hasUnsavedChanges[i]);
        if (changedFields.length > 0) {
            try {
                await updateManyFormFieldsAsync(changedFields);
                setHasUnsavedChanges({});
            } catch (error) {
                console.error('Failed to save fields:', error);
                return;
            }
        }
        onCancel();
    };

    const handleSaveAndActivate = async () => {
        if (!validate()) return;
        const changedFields = fields.filter((_, i) => hasUnsavedChanges[i]);
        if (changedFields.length > 0) {
            try {
                await updateManyFormFieldsAsync(changedFields);
            } catch (error) {
                console.error('Failed to save fields:', error);
                return;
            }
        }
        if (draftVersion?.formVersionId && form?.formId) {
            try {
                await activateFormVersionAsync(form.formId, draftVersion.formVersionId);
                onCancel();
            } catch (error) {
                console.error('Failed to activate version:', error);
            }
        }
    };

    // ── Drag handlers ────────────────────────────────────────────────
    const handleDragStart = (e, index) => {
        dragIndexRef.current = index;
        e.dataTransfer.effectAllowed = 'move';
        // slight delay so the ghost image renders before the element fades
        setTimeout(() => setDragOverIndex(index), 0);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (index !== dragOverIndex) setDragOverIndex(index);
    };

    const handleDragEnd = () => {
        dragIndexRef.current = null;
        setDragOverIndex(null);
    };

    const handleDrop = async (e, targetIndex) => {
        e.preventDefault();
        const sourceIndex = dragIndexRef.current;
        dragIndexRef.current = null;
        setDragOverIndex(null);

        if (sourceIndex === null || sourceIndex === targetIndex) return;

        const sourceField = fields[sourceIndex];
        const targetField = fields[targetIndex];

        // Optimistic reorder
        const reordered = [...fields];
        const [moved] = reordered.splice(sourceIndex, 1);
        reordered.splice(targetIndex, 0, moved);
        setFields(reordered);

        if (moveFormFieldsAsync && sourceField?.formFieldId && targetField?.formFieldId) {
            setIsReordering(true);
            try {
                await moveFormFieldsAsync(sourceField.formFieldId, targetField.formFieldId);
            } catch (error) {
                console.error('Failed to reorder fields:', error);
                // Revert on failure
                setFields(fields);
            } finally {
                setIsReordering(false);
            }
        }
    };
    // ─────────────────────────────────────────────────────────────────

    if (creating) {
        return (
            <MuiCore.Box className={classes.section}>
                <MuiCore.Box style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                    <MuiCore.CircularProgress style={{ color: '#7F2062' }} />
                    <MuiCore.Typography style={{ marginLeft: 16, fontFamily: 'Sofia' }}>
                        Creating draft version...
                    </MuiCore.Typography>
                </MuiCore.Box>
            </MuiCore.Box>
        );
    }

    return (
        <MuiCore.Box className={classes.section}>
            <MuiCore.Box className={classes.sectionHeader}>
                <MuiCore.Box className={classes.sectionTitle}>
                    <InfoIcon />
                    <MuiCore.Typography variant="h6" style={{ fontFamily: 'Sofia-bold' }}>Edit Call Form</MuiCore.Typography>
                </MuiCore.Box>
                {isReordering && (
                    <MuiCore.Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MuiCore.CircularProgress size={16} style={{ color: '#7F2062' }} />
                        <MuiCore.Typography variant="caption" style={{ fontFamily: 'Sofia', color: '#7F2062' }}>
                            Reordering...
                        </MuiCore.Typography>
                    </MuiCore.Box>
                )}
            </MuiCore.Box>

            <MuiCore.Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, fontFamily: 'Sofia' }}>
                Customize the information that your Grasshopper AI will gather. These fields will be included on any messages
            </MuiCore.Typography>

            {/* Field Cards */}
            <MuiCore.Box style={{ marginBottom: 8 }}>
                {fields.map((field, index) => {
                    const isDraggingThis = dragIndexRef.current === index;
                    const isDragTarget = dragOverIndex === index && dragIndexRef.current !== index;
                    return (
                        <MuiCore.Paper
                            key={field.formFieldId || index}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                            style={{
                                marginBottom: 16,
                                borderRadius: 12,
                                border: isDragTarget
                                    ? '2px solid #7F2062'
                                    : '1px solid #cccdd2',
                                opacity: isDraggingThis ? 0.5 : 1,
                                transition: 'border 0.15s ease, opacity 0.15s ease',
                                cursor: 'default',
                            }}
                            elevation={isDragTarget ? 4 : 1}
                        >
                            {/* Card Header: drag handle + title + delete */}
                            <MuiCore.Box
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '12px 16px 0 16px',
                                    borderBottom: '1px solid #f0f0f0',
                                    paddingBottom: 12,
                                }}
                            >
                                <MuiCore.Box
                                    style={{
                                        cursor: 'grab',
                                        color: '#bdbdbd',
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginRight: 8,
                                        '&:active': { cursor: 'grabbing' }
                                    }}
                                    title="Drag to reorder"
                                >
                                    <DragIndicatorIcon />
                                </MuiCore.Box>
                                <MuiCore.Typography
                                    variant="h6"
                                    style={{ fontFamily: 'Sofia', fontWeight: 700, flex: 1 }}
                                >
                                    Field {index + 1}
                                </MuiCore.Typography>
                                <MuiCore.IconButton
                                    onClick={() => handleDeleteField(index)}
                                    size="small"
                                    style={{ color: '#9e9e9e' }}
                                >
                                    <DeleteIcon />
                                </MuiCore.IconButton>
                            </MuiCore.Box>

                            {/* Card Body */}
                            <MuiCore.Box style={{ padding: '16px 16px 8px 16px' }}>
                                <MuiCore.Grid container spacing={2} alignItems="flex-start">
                                    {/* Name field */}
                                    <MuiCore.Grid item xs={12} sm={6}>
                                        <MuiCore.TextField
                                            fullWidth
                                            label="Field Name *"
                                            value={field.label || ''}
                                            onChange={(e) => handleFieldChange(index, 'label', e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            error={!!validationErrors[index]}
                                            helperText={validationErrors[index] || ''}
                                            inputProps={{ maxLength: 25, style: { fontFamily: 'Sofia' } }}
                                            InputLabelProps={{ style: { fontFamily: 'Sofia' } }}
                                            InputProps={{ style: { fontFamily: 'Sofia' } }}
                                        />
                                        <MuiCore.Typography
                                            variant="caption"
                                            style={{
                                                display: 'block',
                                                marginTop: 4,
                                                color: (field.label || '').length > 25 ? '#d32f2f' : '#666',
                                                fontFamily: 'Sofia'
                                            }}
                                        >
                                            {(field.label || '').length}/25 Characters
                                        </MuiCore.Typography>
                                    </MuiCore.Grid>

                                    {/* Type dropdown */}
                                    <MuiCore.Grid item xs={12} sm={6}>
                                        <MuiCore.FormControl fullWidth variant="outlined" size="small">
                                            <MuiCore.InputLabel style={{ fontFamily: 'Sofia' }}>Type *</MuiCore.InputLabel>
                                            <MuiCore.Select
                                                value={field.fieldType || 'text'}
                                                onChange={(e) => handleFieldChange(index, 'fieldType', e.target.value)}
                                                label="Type *"
                                                style={{ fontFamily: 'Sofia' }}
                                                renderValue={(value) => {
                                                    const found = fieldTypes.find(t => t.value === value);
                                                    const RenderIcon = found?.Icon || TextFieldsIcon;
                                                    return (
                                                        <MuiCore.Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <RenderIcon fontSize="small" style={{ color: '#555' }} />
                                                            <span style={{ fontFamily: 'Sofia' }}>{found?.label || value}</span>
                                                        </MuiCore.Box>
                                                    );
                                                }}
                                            >
                                                {fieldTypes.map(type => (
                                                    <MuiCore.MenuItem key={type.value} value={type.value} style={{ fontFamily: 'Sofia' }}>
                                                        <MuiCore.Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <type.Icon fontSize="small" style={{ color: '#555' }} />
                                                            {type.label}
                                                        </MuiCore.Box>
                                                    </MuiCore.MenuItem>
                                                ))}
                                            </MuiCore.Select>
                                        </MuiCore.FormControl>
                                    </MuiCore.Grid>

                                    {/* Required checkbox */}
                                    <MuiCore.Grid item xs={6}>
                                        <MuiCore.FormControlLabel
                                            control={
                                                <MuiCore.Checkbox
                                                    checked={field.required === 'Y'}
                                                    onChange={(e) => handleFieldChange(index, 'required', e.target.checked ? 'Y' : 'N')}
                                                    style={{ color: field.required === 'Y' ? '#E57C2B' : undefined }}
                                                />
                                            }
                                            label={
                                                <MuiCore.Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span style={{ fontFamily: 'Sofia' }}>Required</span>
                                                    <MuiCore.Tooltip title="When checked, your Grasshopper AI will require the caller to provide this information before sending the message">
                                                        <InfoIcon style={{ fontSize: 16, color: '#bdbdbd' }} />
                                                    </MuiCore.Tooltip>
                                                </MuiCore.Box>
                                            }
                                        />
                                    </MuiCore.Grid>

                                    {/* Verify checkbox */}
                                    <MuiCore.Grid item xs={6}>
                                        <MuiCore.FormControlLabel
                                            control={
                                                <MuiCore.Checkbox
                                                    checked={field.verify === 'Y'}
                                                    onChange={(e) => handleFieldChange(index, 'verify', e.target.checked ? 'Y' : 'N')}
                                                    style={{ color: field.verify === 'Y' ? '#E57C2B' : undefined }}
                                                />
                                            }
                                            label={
                                                <MuiCore.Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span style={{ fontFamily: 'Sofia' }}>Verify</span>
                                                    <MuiCore.Tooltip title="When checked, your Grasshopper AI will read this information back to the caller to verify it is correct">
                                                        <InfoIcon style={{ fontSize: 16, color: '#bdbdbd' }} />
                                                    </MuiCore.Tooltip>
                                                </MuiCore.Box>
                                            }
                                        />
                                    </MuiCore.Grid>
                                </MuiCore.Grid>
                            </MuiCore.Box>
                        </MuiCore.Paper>
                    );
                })}
            </MuiCore.Box>

            {/* Add Field Button */}
            <MuiCore.Button
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={handleAddField}
                style={{
                    marginBottom: 16,
                    fontFamily: 'Sofia',
                    textTransform: 'none',
                    borderRadius: 100,
                    borderColor: '#7F2062',
                    color: '#7F2062',
                }}
            >
                Add Field
            </MuiCore.Button>

            {/* Action Buttons */}
            <MuiCore.Box className={classes.actionButtons} style={{ marginTop: 8 }}>
                <MuiCore.Button
                    startIcon={<CloseIcon />}
                    onClick={onCancel}
                    variant="outlined"
                    style={{ fontFamily: 'Sofia', textTransform: 'none', borderRadius: 100 }}
                >
                    Cancel
                </MuiCore.Button>
                <MuiCore.Button
                    startIcon={<CheckIcon />}
                    onClick={handleSaveDraft}
                    variant="outlined"
                    style={{ fontFamily: 'Sofia', textTransform: 'none', borderRadius: 100 }}
                >
                    Save as Draft
                </MuiCore.Button>
                <MuiCore.Button
                    startIcon={<CheckIcon />}
                    onClick={handleSaveAndActivate}
                    variant="contained"
                    style={{ backgroundColor: '#7F2062', color: '#fff', fontFamily: 'Sofia', fontWeight: 700, textTransform: 'none', borderRadius: 100 }}
                >
                    Save &amp; Activate
                </MuiCore.Button>
            </MuiCore.Box>
        </MuiCore.Box>
    );
};

export default FormFieldsFormCard;
